/**
 * GroupOverlay - iOS-style folder overlay that shows all apps in a group
 */

"use client";

import {
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { IconGroup } from "@repo/types";
import type { DesktopIconConfig } from "../types";
import { useAppBadgeCounts } from "../hooks/useAppBadgeCounts";

export interface GroupOverlayProps {
  /** The group to display */
  group: IconGroup;
  /** Resolved icon configs for icons in this group */
  iconConfigs: DesktopIconConfig[];
  /** Bounding rect of the group icon (for origin animation) */
  anchorRect: DOMRect | null;
  /** Close the overlay */
  onClose: () => void;
  /** Rename the group */
  onRename: (name: string) => void;
  /** Click an icon to launch it */
  onIconClick: (config: DesktopIconConfig) => void;
  /** Remove an icon from the group (drag out) */
  onRemoveIcon: (iconId: string) => void;
  /** Reorder icons within the group */
  onReorderIcons: (newOrder: string[]) => void;
}

export function GroupOverlay({
  group,
  iconConfigs,
  anchorRect,
  onClose,
  onRename,
  onIconClick,
  onRemoveIcon,
  onReorderIcons,
}: GroupOverlayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Jiggle mode state
  const [isJiggling, setIsJiggling] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drag state (works in both jiggle and non-jiggle modes)
  const [draggedIconId, setDraggedIconId] = useState<string | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragGhostRef = useRef<HTMLDivElement>(null);
  const [dragGhostPos, setDragGhostPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Track which icon index we're hovering over for reorder
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // Animate in
  useEffect(() => {
    requestAnimationFrame(() => setIsOpen(true));
  }, []);

  // Focus input when editing
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClose = useCallback(() => {
    setIsJiggling(false);
    setIsOpen(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  const handleNameSubmit = useCallback(() => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== group.name) {
      onRename(trimmed);
    } else {
      setEditName(group.name);
    }
    setIsEditing(false);
  }, [editName, group.name, onRename]);

  // Badge counts for individual icons inside the group
  const badgeCounts = useAppBadgeCounts();

  // Grid columns: 3 for <=9, 4 for more
  const cols = iconConfigs.length > 9 ? 4 : 3;

  // Animation origin
  const originX = anchorRect
    ? anchorRect.left + anchorRect.width / 2
    : window.innerWidth / 2;
  const originY = anchorRect
    ? anchorRect.top + anchorRect.height / 2
    : window.innerHeight / 2;

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Compute which grid index the touch position maps to
  const getHoverIndex = useCallback(
    (x: number, y: number): number | null => {
      if (!gridRef.current) return null;
      const gridRect = gridRef.current.getBoundingClientRect();
      if (
        x < gridRect.left ||
        x > gridRect.right ||
        y < gridRect.top ||
        y > gridRect.bottom
      )
        return null;
      const colWidth = gridRect.width / cols;
      const cells = gridRef.current.children;
      // Find which cell center is closest
      for (let i = 0; i < cells.length; i++) {
        const cellRect = (cells[i] as HTMLElement).getBoundingClientRect();
        if (
          x >= cellRect.left - colWidth * 0.3 &&
          x <= cellRect.right + colWidth * 0.3 &&
          y >= cellRect.top - 8 &&
          y <= cellRect.bottom + 8
        ) {
          return i;
        }
      }
      return null;
    },
    [cols]
  );

  const handleTouchStart = useCallback(
    (iconId: string, e: React.TouchEvent) => {
      const touch = e.touches[0];
      dragStartRef.current = { x: touch.clientX, y: touch.clientY };

      if (isJiggling) {
        // In jiggle mode: start drag immediately
        setDraggedIconId(iconId);
        setDragGhostPos({ x: touch.clientX, y: touch.clientY });
      } else {
        // Not in jiggle mode: start long-press timer
        clearLongPress();
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          setIsJiggling(true);
          setDraggedIconId(iconId);
          setDragGhostPos({ x: touch.clientX, y: touch.clientY });
          if (navigator.vibrate) navigator.vibrate(50);
        }, 500);
      }
    },
    [isJiggling, clearLongPress]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];

      // Cancel long-press if moved too far
      if (longPressTimerRef.current && dragStartRef.current) {
        const dx = touch.clientX - dragStartRef.current.x;
        const dy = touch.clientY - dragStartRef.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > 10) {
          clearLongPress();
        }
      }

      if (!draggedIconId || !dragStartRef.current) return;

      // Update ghost position
      if (dragGhostRef.current) {
        dragGhostRef.current.style.transform = `translate(${touch.clientX}px, ${touch.clientY}px) translate(-50%, -50%) scale(1.1)`;

        // Fade when outside card bounds
        if (cardRef.current) {
          const cardRect = cardRef.current.getBoundingClientRect();
          const isOutside =
            touch.clientX < cardRect.left - 20 ||
            touch.clientX > cardRect.right + 20 ||
            touch.clientY < cardRect.top - 20 ||
            touch.clientY > cardRect.bottom + 20;
          dragGhostRef.current.style.opacity = isOutside ? "0.5" : "0.9";
        }
      }

      // Track hover index for reorder preview
      if (isJiggling) {
        const idx = getHoverIndex(touch.clientX, touch.clientY);
        setHoverIndex(idx);
      }
    },
    [draggedIconId, isJiggling, clearLongPress, getHoverIndex]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      clearLongPress();

      if (!draggedIconId || !dragStartRef.current || !cardRef.current) {
        setDraggedIconId(null);
        setDragGhostPos(null);
        dragStartRef.current = null;
        setHoverIndex(null);
        return;
      }

      const touch = e.changedTouches[0];
      const cardRect = cardRef.current.getBoundingClientRect();
      const isOutside =
        touch.clientX < cardRect.left - 20 ||
        touch.clientX > cardRect.right + 20 ||
        touch.clientY < cardRect.top - 20 ||
        touch.clientY > cardRect.bottom + 20;

      if (isOutside) {
        // Drag out → remove from group
        onRemoveIcon(draggedIconId);
      } else if (isJiggling && hoverIndex !== null) {
        // Reorder: move dragged icon to hover position
        const currentOrder = iconConfigs.map((c) => c.id);
        const fromIdx = currentOrder.indexOf(draggedIconId);
        if (fromIdx >= 0 && hoverIndex !== fromIdx) {
          const newOrder = [...currentOrder];
          newOrder.splice(fromIdx, 1);
          newOrder.splice(hoverIndex, 0, draggedIconId);
          onReorderIcons(newOrder);
        }
      }

      setDraggedIconId(null);
      setDragGhostPos(null);
      dragStartRef.current = null;
      setHoverIndex(null);
    },
    [
      draggedIconId,
      isJiggling,
      hoverIndex,
      iconConfigs,
      onRemoveIcon,
      onReorderIcons,
      clearLongPress,
    ]
  );

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center"
      style={{
        backgroundColor: isOpen ? "rgba(0, 0, 0, 0.2)" : "transparent",
        backdropFilter: isOpen ? "blur(30px) saturate(1.8)" : "none",
        WebkitBackdropFilter: isOpen ? "blur(30px) saturate(1.8)" : "none",
        transition: "background-color 0.2s ease, backdrop-filter 0.2s ease",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (isJiggling) {
            setIsJiggling(false);
          } else {
            handleClose();
          }
        }
      }}
    >
      <div
        ref={cardRef}
        className="relative mx-6 max-h-[70vh] w-full max-w-[320px] overflow-y-auto rounded-3xl p-6"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          transformOrigin: `${originX}px ${originY}px`,
          transform: isOpen ? "scale(1)" : "scale(0.3)",
          opacity: isOpen ? 1 : 0,
          transition:
            "transform 0.25s cubic-bezier(0.2, 0, 0, 1.1), opacity 0.2s ease",
        }}
      >
        {/* Group name */}
        <div className="mb-4 text-center">
          {isEditing ? (
            <input
              ref={inputRef}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNameSubmit();
                if (e.key === "Escape") {
                  setEditName(group.name);
                  setIsEditing(false);
                }
              }}
              className="w-full rounded-lg bg-white/10 px-3 py-1.5 text-center text-lg font-semibold text-white outline-none ring-2 ring-white/30 focus:ring-white/50"
            />
          ) : (
            <button
              onClick={() => {
                setEditName(group.name);
                setIsEditing(true);
              }}
              className="text-lg font-semibold text-white transition-opacity hover:opacity-80"
            >
              {group.name}
            </button>
          )}
        </div>

        {/* Icon grid */}
        <div
          ref={gridRef}
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            justifyItems: "center",
          }}
        >
          {iconConfigs.map((config, idx) => {
            const icon = config.icon;
            const isString = typeof icon === "string";
            const isElement = isValidElement(icon);
            const isDragged = config.id === draggedIconId;
            const iconBadge = badgeCounts.get(config.id) ?? 0;
            const isHoverTarget =
              hoverIndex === idx && draggedIconId && !isDragged;

            // Jiggle delay per icon (stable via id hash)
            const jiggleDelay = isJiggling
              ? `${(config.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 5) * 0.06}s`
              : undefined;

            return (
              <div
                key={config.id}
                className={`flex w-[72px] select-none flex-col items-center ${
                  isDragged
                    ? ""
                    : isJiggling
                      ? ""
                      : "cursor-pointer active:scale-90"
                } ${isJiggling && !isDragged ? "animate-jiggle" : "transition-transform duration-100"}`}
                style={{
                  opacity: isDragged ? 0 : 1,
                  animationDelay: jiggleDelay,
                  transform: isHoverTarget ? "scale(0.85)" : undefined,
                  transition: isHoverTarget
                    ? "transform 0.15s ease"
                    : undefined,
                }}
                onClick={() => {
                  if (!draggedIconId && !isJiggling) onIconClick(config);
                }}
                onTouchStart={(e) => handleTouchStart(config.id, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="relative">
                  <div
                    className={`h-[60px] w-[60px] overflow-hidden rounded-[16px] ${
                      isString
                        ? "flex items-center justify-center bg-white/10 text-4xl shadow-lg backdrop-blur-sm"
                        : ""
                    } ${
                      isElement
                        ? "[&>div]:h-full [&>div]:w-full [&>div]:rounded-[14px] [&_svg]:h-10 [&_svg]:w-10"
                        : ""
                    }`}
                  >
                    {isString && icon}
                    {isElement && icon}
                  </div>
                  {/* Delete badge in jiggle mode */}
                  {isJiggling && !isDragged && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveIcon(config.id);
                      }}
                      className="absolute -left-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-gray-500/80 text-[10px] font-bold text-white shadow-md backdrop-blur-sm"
                    >
                      ✕
                    </button>
                  )}
                  {/* Notification badge */}
                  {!isJiggling && (
                    <div
                      className={`pointer-events-none absolute -right-[6px] -top-[6px] z-10 flex min-w-[19px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[11px] font-bold leading-[17px] text-white transition-all duration-200 ${iconBadge > 0 ? "scale-100" : "scale-0"}`}
                    >
                      {iconBadge > 99 ? "99+" : iconBadge}
                    </div>
                  )}
                </div>
                <span className="mt-1 w-full truncate text-center text-[11px] font-medium leading-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                  {config.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating drag ghost — rendered above the overlay backdrop */}
      {draggedIconId &&
        dragGhostPos &&
        (() => {
          const dragConfig = iconConfigs.find((c) => c.id === draggedIconId);
          if (!dragConfig) return null;
          const dIcon = dragConfig.icon;
          const dIsString = typeof dIcon === "string";
          const dIsElement = isValidElement(dIcon);
          return (
            <div
              ref={dragGhostRef}
              style={{
                position: "fixed",
                left: 0,
                top: 0,
                transform: `translate(${dragGhostPos.x}px, ${dragGhostPos.y}px) translate(-50%, -50%) scale(1.1)`,
                willChange: "transform",
                zIndex: 10001,
                pointerEvents: "none",
                filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.4))",
              }}
            >
              <div className="flex w-[72px] flex-col items-center">
                <div
                  className={`h-[60px] w-[60px] overflow-hidden rounded-[16px] ${
                    dIsString
                      ? "flex items-center justify-center bg-white/10 text-4xl shadow-lg backdrop-blur-sm"
                      : ""
                  } ${
                    dIsElement
                      ? "[&>div]:h-full [&>div]:w-full [&>div]:rounded-[14px] [&_svg]:h-10 [&_svg]:w-10"
                      : ""
                  }`}
                >
                  {dIsString && dIcon}
                  {dIsElement && dIcon}
                </div>
                <span className="mt-1 w-full truncate text-center text-[11px] font-medium leading-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                  {dragConfig.label}
                </span>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
